import React from 'react';
import classnames from 'classnames';
import ColumnAligner from '@intelligence/v3/components/TableComponents/ColumnAligner';
import { formatDateStrPlus } from '@home/utils/date';
import { getHeaderWidthIntelligence, getHeaderIntelligence, getSimpleSortableHeaderIntelligence } from './utils';
import { LANGUAGE_EN } from '@home/_config/config';

import type { TableColumn } from 'tea-component';
import type { IDateFormat } from '@home/types/common';
import type { IColumnBaseOption, IColumnHeaderOption, IColumnSimpleSortOption } from './utils';

// Date Column构建器自定义配置
interface IDateColumnBuilderOptionCore {
  // 列单元格的className
  className?: string;
  // 占位符
  placeholder?: IPlaceholder;
  // 日期格式化令牌 或 自定义日期格式化方法
  dateFormat?: IDateFormat;
}

type IDateColumnBuilderOption<T> = Partial<TableColumn<T>> &
IColumnBaseOption &
IColumnHeaderOption &
IColumnSimpleSortOption &
IDateColumnBuilderOptionCore;

/**
 * 日期Column构建器
 * @param dateColumnBuilderOption 日期Column构建器配置
 * @param dateColumnBuilderOption.className 列单元格的className
 * @param dateColumnBuilderOption.placeholder 占位符
 * @param dateColumnBuilderOption.dateFormat 日期格式化令牌 或 自定义日期格式化方法
 */
const DateColumnBuilder = <T extends IRecord = IRecord>(
  dateColumnBuilderOption?: IDateColumnBuilderOption<T>,
): TableColumn<T> => {
  const {
    className,
    placeholder = '-',
    locale = LANGUAGE_EN,
    key = 'date',
    headerTranslatable = true,
    headerTranslateKey = 'dbi_date',
    header = 'Date',
    align = 'left',
    fixed,
    width = 126,
    widthCN,
    dateFormat,
    ...sortOption
  } = dateColumnBuilderOption || {};

  const headerWidth = getHeaderWidthIntelligence(locale, { width, widthCN });
  const headerText = getHeaderIntelligence(locale, { headerTranslatable, headerTranslateKey, header });

  return {
    key,
    align,
    fixed,
    width: headerWidth,
    header: (
      <ColumnAligner align={align} width={headerWidth}>
        {getSimpleSortableHeaderIntelligence({ ...sortOption, key, align, header: headerText })}
      </ColumnAligner>
    ),
    render: (cvm) => {
      const date = cvm?.[key];
      const dateFormatted = formatDateStrPlus(date, dateFormat, placeholder);
      return (
        <ColumnAligner align={align} width={headerWidth}>
          <div className={classnames('dbi-common-column-date', className)}>{dateFormatted}</div>
        </ColumnAligner>
      );
    },
  };
};

export default DateColumnBuilder;
